import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  InputAdornment,
  IconButton,
  Collapse,
  Tooltip,
  Stack,
  Button,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import {
  auditLogsApi,
  AuditLog,
  AuditLogFilters,
  AUDIT_MODULES,
  AUDIT_ACTIONS,
} from '../api/auditLogs';
import { ACTION_CHIP } from '../components/AuditActionChip';

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(dateStr: string): { date: string; time: string } {
  const d = new Date(dateStr);
  return {
    date: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
  };
}

function parseJsonSafe(val: string | null): string {
  if (!val) return '—';
  try {
    const parsed = JSON.parse(val);
    return typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : String(parsed);
  } catch {
    return val;
  }
}

const MODULE_LABEL: Record<string, string> = Object.fromEntries(
  AUDIT_MODULES.map(m => [m.value, m.label])
);

// ─── Row component ────────────────────────────────────────────────────────────

function AuditRow({ log }: { log: AuditLog }) {
  const [open, setOpen] = useState(false);
  const chip = ACTION_CHIP[log.action] || { label: log.action, color: 'default' as const };
  const { date, time } = formatDateTime(log.created_at);
  const hasDiff = log.field_name || log.old_value || log.new_value;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: hasDiff ? 'pointer' : 'default', '& > td': { borderBottom: open ? 0 : undefined } }}
        onClick={() => hasDiff && setOpen(o => !o)}
      >
        {/* Timestamp */}
        <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', py: 1 }}>
          <Typography variant="body2" fontWeight={500}>{date}</Typography>
          <Typography variant="caption" color="text.secondary">{time}</Typography>
        </TableCell>

        {/* User */}
        <TableCell sx={{ verticalAlign: 'top', py: 1 }}>
          <Typography variant="body2" noWrap>{log.user_name}</Typography>
          <Typography variant="caption" color="text.disabled" display="block" noWrap>
            {log.user_role}
          </Typography>
        </TableCell>

        {/* Module */}
        <TableCell sx={{ verticalAlign: 'top', py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {MODULE_LABEL[log.module] || log.module}
          </Typography>
        </TableCell>

        {/* Action chip */}
        <TableCell sx={{ verticalAlign: 'top', py: 1 }}>
          <Chip
            label={chip.label}
            size="small"
            color={chip.color}
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.72rem' }}
          />
        </TableCell>

        {/* Entity */}
        <TableCell sx={{ verticalAlign: 'top', py: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            {log.entity_label || log.entity_id}
          </Typography>
          <Typography variant="caption" color="text.disabled" display="block">
            {log.entity_type}
          </Typography>
        </TableCell>

        {/* Summary + expand */}
        <TableCell sx={{ verticalAlign: 'top', py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {log.summary || '—'}
            </Typography>
            {hasDiff && (
              <IconButton size="small" sx={{ mt: -0.25, flexShrink: 0 }}>
                {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
          </Box>
        </TableCell>
      </TableRow>

      {/* Expandable diff row */}
      {hasDiff && (
        <TableRow>
          <TableCell colSpan={6} sx={{ py: 0, px: 2, bgcolor: 'grey.50' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {log.field_name && (
                  <Box>
                    <Typography variant="caption" color="text.disabled" fontWeight={600} display="block">
                      FIELD
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {log.field_name}
                    </Typography>
                  </Box>
                )}
                {log.old_value && (
                  <Box>
                    <Typography variant="caption" color="error.main" fontWeight={600} display="block">
                      BEFORE
                    </Typography>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{
                        bgcolor: 'error.50',
                        px: 1, py: 0.5,
                        borderRadius: 1,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        maxWidth: 300,
                        fontSize: '0.78rem',
                      }}
                    >
                      {parseJsonSafe(log.old_value)}
                    </Typography>
                  </Box>
                )}
                {log.new_value && (
                  <Box>
                    <Typography variant="caption" color="success.dark" fontWeight={600} display="block">
                      AFTER
                    </Typography>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{
                        bgcolor: 'success.50',
                        px: 1, py: 0.5,
                        borderRadius: 1,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        maxWidth: 300,
                        fontSize: '0.78rem',
                      }}
                    >
                      {parseJsonSafe(log.new_value)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AuditLogPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [q, setQ] = useState('');
  const [module, setModule] = useState('');
  const [action, setAction] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(0);        // MUI TablePagination is 0-indexed
  const [pageSize, setPageSize] = useState(25);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: AuditLogFilters = {
        page: page + 1,  // backend is 1-indexed
        pageSize,
        q:         q        || undefined,
        module:    module   || undefined,
        action:    action   || undefined,
        from_date: fromDate || undefined,
        to_date:   toDate   || undefined,
      };
      const result = await auditLogsApi.getAll(filters);
      setLogs(result.data);
      setTotal(result.total);
    } catch {
      setError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [q, module, action, fromDate, toDate, page, pageSize]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const clearFilters = () => {
    setQ('');
    setModule('');
    setAction('');
    setFromDate('');
    setToDate('');
    setPage(0);
  };

  const hasFilters = q || module || action || fromDate || toDate;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <ManageSearchIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Audit Log</Typography>
          <Typography variant="body2" color="text.secondary">
            Track every create, edit and delete action across all modules.
          </Typography>
        </Box>
      </Box>

      {/* Filter bar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search by entity, user, summary…"
            value={q}
            onChange={e => { setQ(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: q ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQ('')}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ flex: '3 1 220px' }}
          />

          {/* Module */}
          <FormControl size="small" sx={{ flex: '1 1 160px' }}>
            <InputLabel>Module</InputLabel>
            <Select value={module} label="Module" onChange={e => { setModule(e.target.value); setPage(0); }}>
              <MenuItem value="">All modules</MenuItem>
              {AUDIT_MODULES.map(m => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Action */}
          <FormControl size="small" sx={{ flex: '1 1 150px' }}>
            <InputLabel>Action</InputLabel>
            <Select value={action} label="Action" onChange={e => { setAction(e.target.value); setPage(0); }}>
              <MenuItem value="">All actions</MenuItem>
              {AUDIT_ACTIONS.map(a => (
                <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date range */}
          <TextField
            size="small"
            label="From"
            type="date"
            value={fromDate}
            onChange={e => { setFromDate(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 140px' }}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            value={toDate}
            onChange={e => { setToDate(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 140px' }}
          />

          {hasFilters && (
            <Button size="small" variant="outlined" color="inherit" onClick={clearFilters} startIcon={<ClearIcon />}>
              Clear
            </Button>
          )}
        </Stack>

        {/* Active filter chips */}
        {hasFilters && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
            {module  && <Chip size="small" label={`Module: ${MODULE_LABEL[module] || module}`} onDelete={() => setModule('')} />}
            {action  && <Chip size="small" label={`Action: ${action}`} onDelete={() => setAction('')} />}
            {fromDate && <Chip size="small" label={`From: ${fromDate}`} onDelete={() => setFromDate('')} />}
            {toDate  && <Chip size="small" label={`To: ${toDate}`}   onDelete={() => setToDate('')} />}
          </Box>
        )}
      </Paper>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Count row */}
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading…' : `${total.toLocaleString()} record${total !== 1 ? 's' : ''}`}
          </Typography>
          {loading && <CircularProgress size={14} />}
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap', width: 110 }}>TIMESTAMP</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: 130 }}>USER</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: 140 }}>MODULE</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: 130 }}>ACTION</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: 150 }}>ENTITY</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>SUMMARY</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <ManageSearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1, display: 'block', mx: 'auto' }} />
                    <Typography color="text.secondary">No audit log entries found.</Typography>
                    {hasFilters && (
                      <Button size="small" sx={{ mt: 1 }} onClick={clearFilters}>Clear filters</Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
              {logs.map(log => <AuditRow key={log.id} log={log} />)}
            </TableBody>
          </Table>
        </Box>

        <Divider />
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
}
